// Lib
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { getNewReminderOption } from '../../task/pills/assignment/dueDate/taskDueDateReminderUtils';
import { getAreDatesSameDay } from '../../../../common/utils/timeUtil';

// Selectors
import { userLanguagePreferenceSelector } from '../../../user/currentUserSelector';

// Components
import Calendar from '../../../components/datetime/Calendar';
import DueDateTimePicker from '../../task/pills/assignment/dueDate/DueDateTimePicker';
import DueDateReminderSelect from '../../task/pills/assignment/dueDate/DueDateReminderSelect';

// Constants
import { REMINDER_VALUES } from '../../../../common/reminders/reminderConstants';

// Types
import type { TaskItemDueDate } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';

// Styles - shared with the element due-date popup, since both render the same layout.
import '../../task/pills/assignment/dueDate/TaskDueDatePopupContent.scss';

const getNewDate = () => {
    const newDate = new Date();

    // By default, make it due at 5pm
    newDate.setHours(17);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    return newDate;
};

type TaskItemDueDatePopupContentProps = {
    /** The owning task node's `dueDate` attr, `null` when unset. */
    dueDate: TaskItemDueDate | null;
    onChange: (dueDate: TaskItemDueDate) => void;
    onClear: () => void;
};

/**
 * Node-attr backed due-date picker content for a task item chip. Mirrors the
 * element `TaskDueDatePopupContent`, but is driven by the owning node's attrs
 * (via props supplied by `TaskItemChipsContainer`) and commits each change
 * directly (no temporary/final redux split), since node-attr writes are already
 * undoable via the editor's own history.
 */
const TaskItemDueDatePopupContent: React.FC<TaskItemDueDatePopupContentProps> = ({
    dueDate: dueDateAttr,
    onChange,
    onClear,
}) => {
    const userLanguagePreference = useSelector(userLanguagePreferenceSelector);
    const [userPersistedDueReminder, setUserPersistedDueReminder] = useState<string | null>(null);

    const hasDueDate = !!dueDateAttr;
    const hasDueDateTime = dueDateAttr?.hasTime ?? false;
    const dueDate = dueDateAttr ? new Date(dueDateAttr.timestamp) : getNewDate();
    const dueReminder = dueDateAttr?.reminder ?? REMINDER_VALUES.NINE_AM_DAY_OF;

    // Picking a day on the calendar sets the due date; re-picking the already-selected
    // day clears it instead (matches the element popup's calendar behaviour).
    const onCalendarChange = (updatedDate: Date) => {
        // Clicking the selected date should remove the due date, but not close the popup
        if (hasDueDate && getAreDatesSameDay(updatedDate, dueDate)) {
            onClear();
            return;
        }

        const newReminder = getNewReminderOption({
            dueDate: updatedDate,
            hasDueDateTime,
            dueReminder,
            userPersistedDueReminder,
        });
        onChange({ timestamp: updatedDate.getTime(), hasTime: hasDueDateTime, reminder: newReminder });
    };

    // Toggles whether the due date carries a specific time of day, re-deriving the
    // reminder since some reminder options only make sense once a time is set.
    const updateHasDueDateTime = (updatedHasDueDateTime: boolean) => {
        const newReminder = getNewReminderOption({
            dueDate,
            hasDueDateTime: updatedHasDueDateTime,
            dueReminder,
            userPersistedDueReminder,
        });
        onChange({ timestamp: dueDate.getTime(), hasTime: updatedHasDueDateTime, reminder: newReminder });
    };

    // Applies a new time of day to the current due date from the time picker.
    const updateDueDate = (updatedDate: Date) => {
        const newReminder = getNewReminderOption({
            dueDate: updatedDate,
            hasDueDateTime,
            dueReminder,
            userPersistedDueReminder,
        });
        onChange({ timestamp: updatedDate.getTime(), hasTime: hasDueDateTime, reminder: newReminder });
    };

    // Applies the user's chosen reminder option, remembering it so later date/time
    // edits don't silently override an explicit choice.
    const updateDueReminder = (updatedReminder: string) => {
        setUserPersistedDueReminder(updatedReminder);
        onChange({ timestamp: dueDate.getTime(), hasTime: hasDueDateTime, reminder: updatedReminder });
    };

    return (
        <div className="TaskDueDatePopupContent">
            <div className="calendar-section">
                <Calendar value={dueDate} onChange={onCalendarChange} locale={userLanguagePreference} futureOnly />
            </div>
            <div className="time-section">
                <DueDateTimePicker
                    hasDueDateTime={hasDueDateTime}
                    setHasDueDateTime={updateHasDueDateTime}
                    dueDate={dueDate}
                    setDueDate={updateDueDate}
                />
            </div>
            <div className="reminder-section">
                <DueDateReminderSelect
                    hasDueDateTime={hasDueDateTime}
                    value={dueReminder}
                    onChange={updateDueReminder}
                    locale={userLanguagePreference}
                />
            </div>
        </div>
    );
};

export default TaskItemDueDatePopupContent;
